import type { ReactNode } from 'react';
import { Empty } from './ui.js';

/**
 * Tabela que vira cartão no celular.
 *
 * No desktop é uma tabela comum. Abaixo de 900 px cada linha vira um cartão:
 * a coluna marcada como `principal` vira o título e as demais aparecem como
 * rótulo e valor. Isso evita rolagem lateral, que é o pior padrão em celular.
 */
export interface Coluna<T> {
  chave: string;
  rotulo: string;
  /** `principal` vira título do cartão; `num` alinha à direita; `actions` agrupa botões. */
  tipo?: 'principal' | 'num' | 'actions';
  render: (linha: T) => ReactNode;
  /** Esconde a coluna no celular quando a informação é secundária. */
  ocultarNoCelular?: boolean;
}

export function Tabela<T>({
  colunas,
  linhas,
  chave,
  vazio,
  vazioDetalhe,
}: {
  colunas: Coluna<T>[];
  linhas: T[];
  chave: (linha: T) => string;
  vazio: string;
  vazioDetalhe?: ReactNode;
}) {
  if (linhas.length === 0) {
    return <Empty title={vazio}>{vazioDetalhe}</Empty>;
  }
  return (
    <div className="table-wrap">
      <table className="tabela-cartoes">
        <thead>
          <tr>
            {colunas.map((coluna) => (
              <th key={coluna.chave} className={coluna.tipo === 'num' ? 'num' : undefined}>
                {coluna.tipo === 'actions' ? '' : coluna.rotulo}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {linhas.map((linha) => (
            <tr key={chave(linha)}>
              {colunas.map((coluna) => {
                const classes = [
                  coluna.tipo === 'num' ? 'num' : '',
                  coluna.tipo === 'actions' ? 'actions' : '',
                  coluna.tipo === 'principal' ? 'principal' : '',
                  coluna.ocultarNoCelular ? 'vazia' : '',
                ]
                  .filter(Boolean)
                  .join(' ');
                return (
                  <td key={coluna.chave} data-rotulo={coluna.rotulo} className={classes || undefined}>
                    {coluna.render(linha)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
